import { zodResolver } from '@hookform/resolvers/zod';
import AddRounded from '@mui/icons-material/AddRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { SearchField } from '../../../components/DataTable';
import { EmptyState } from '../../../components/states';
import { useNotify } from '../../../lib/notify';
import { idempotency, keys, usePermissionCatalog, useRoles, type Role } from '../api';
import {
  applyFieldErrors,
  groupPermissions,
  groupState,
  permissionAction,
  plural,
  ROLE_TEMPLATES,
  toggleGroup,
  type PermissionGroup,
} from '../lib';
import { IconTile } from '../ui';

const isOwnerRole = (r: Role) => r.isSystem && r.name === 'Owner';

type Editing = { mode: 'create'; from?: Role } | { mode: 'edit'; role: Role };

export function RolesTab() {
  const { can } = useAuth();
  const canManage = can('roles.manage');
  const qc = useQueryClient();
  const notify = useNotify();
  const { data, isLoading } = useRoles();
  const catalog = usePermissionCatalog();
  const total = catalog.data?.data.length ?? 0;
  const [editing, setEditing] = useState<Editing | null>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; role: Role } | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const roles = [...(data?.data ?? [])].sort(
    (a, b) => Number(isOwnerRole(b)) - Number(isOwnerRole(a)) || a.name.localeCompare(b.name),
  );

  if (isLoading)
    return (
      <RoleGrid>
        {[0, 1, 2].map((i) => (
          <Card key={i} sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1.5}>
              <Skeleton variant="rounded" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="50%" height={24} />
                <Skeleton width="80%" />
              </Box>
            </Stack>
            <Skeleton variant="rounded" height={6} sx={{ mt: 3 }} />
          </Card>
        ))}
      </RoleGrid>
    );

  return (
    <>
      <RoleGrid>
        {roles.map((r) => {
          const owner = isOwnerRole(r);
          const share = total ? Math.min(1, r.permissions.length / total) : 0;
          return (
            <Card
              key={r.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 160ms, transform 160ms',
                '&:hover': { boxShadow: '0 12px 24px -8px rgba(16,24,40,0.14)' },
              }}
            >
              <ButtonBase
                onClick={() => setEditing({ mode: 'edit', role: r })}
                aria-label={`Open role ${r.name}`}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  p: 2.5,
                  pb: 2,
                  flex: 1,
                  borderRadius: 'inherit',
                }}
              >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <IconTile color={owner ? 'primary' : r.isSystem ? 'info' : 'success'}>
                    {owner ? <ShieldRounded /> : <KeyRounded />}
                  </IconTile>
                  <Box sx={{ flex: 1, minWidth: 0, pr: canManage && !owner ? 3 : 0 }}>
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                      <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
                        {r.name}
                      </Typography>
                      {r.isSystem && (
                        <Chip
                          size="small"
                          label="Built-in"
                          sx={{ height: 20, fontSize: 11, fontWeight: 650 }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: 40,
                      }}
                    >
                      {r.description ?? 'No description'}
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ mt: 2.25 }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {owner ? 'Everything' : plural(r.permissions.length, 'permission')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" className="tabular">
                      {Math.round(share * 100)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={share * 100}
                    sx={{ height: 6, borderRadius: 999 }}
                  />
                </Box>
              </ButtonBase>
              <Divider />
              <Stack
                direction="row"
                spacing={1}
                sx={{ px: 2.5, py: 1.25, alignItems: 'center', color: 'text.secondary' }}
              >
                <GroupRounded sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {r.userCount ? plural(r.userCount, 'member') : 'Nobody yet'}
                </Typography>
                {canManage && (
                  <IconButton
                    size="small"
                    aria-label={`More for ${r.name}`}
                    onClick={(e) => setMenu({ el: e.currentTarget, role: r })}
                  >
                    <MoreHorizRounded fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Card>
          );
        })}
        {canManage && (
          <ButtonBase
            onClick={() => setEditing({ mode: 'create' })}
            sx={(t) => ({
              minHeight: 200,
              borderRadius: '14px',
              border: '1.5px dashed',
              borderColor: (t.vars ?? t).palette.divider,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              color: 'text.secondary',
              transition: 'border-color 140ms, color 140ms, background-color 140ms',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: `color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} 4%, transparent)`,
              },
            })}
          >
            <AddRounded sx={{ fontSize: 28 }} />
            <Typography sx={{ fontWeight: 650 }}>Create a role</Typography>
            <Typography variant="caption">Start blank or from a template</Typography>
          </ButtonBase>
        )}
      </RoleGrid>

      {roles.length === 0 && !canManage && (
        <Card>
          <EmptyState
            icon={<KeyRounded />}
            title="No roles yet"
            body="Roles decide what each staff member can see and do."
          />
        </Card>
      )}

      <Menu
        anchorEl={menu?.el}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {menu && !isOwnerRole(menu.role) && (
          <MenuItem
            onClick={() => {
              setEditing({ mode: 'edit', role: menu.role });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            Edit role
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menu) setEditing({ mode: 'create', from: menu.role });
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <ContentCopyRounded fontSize="small" />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        {menu && !menu.role.isSystem && (
          <Tooltip
            title={menu.role.userCount ? 'Remove everyone from this role first' : ''}
            placement="left"
          >
            <span>
              <MenuItem
                disabled={menu.role.userCount > 0}
                onClick={() => {
                  setDeleting(menu.role);
                  setMenu(null);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <DeleteOutlineRounded fontSize="small" />
                </ListItemIcon>
                Delete
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      {editing && <RoleEditorDialog editing={editing} onClose={() => setEditing(null)} />}

      <ConfirmDialog
        open={Boolean(deleting)}
        title={`Delete the ${deleting?.name ?? ''} role?`}
        body="Nobody has this role, so no one loses access. This can’t be undone."
        confirmLabel="Delete role"
        danger
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await unwrap(
              api.DELETE('/roles/{id}', {
                params: { path: { id: deleting.id }, query: { version: deleting.version } },
              }),
            );
            await qc.invalidateQueries({ queryKey: keys.roles });
            notify.success(`${deleting.name} deleted`);
          } catch (e) {
            notify.error(e);
          }
        }}
      />
    </>
  );
}

function RoleGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {children}
    </Box>
  );
}

export const roleSchema = z.object({
  name: z.string().trim().min(1, 'Give the role a name').max(100),
  description: z.string().trim().max(500),
});
type RoleValues = z.infer<typeof roleSchema>;

/** Create, duplicate, edit or (for built-in Owner) view a role with its permission matrix. */
export function RoleEditorDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('md'));
  const { me, can } = useAuth();
  const qc = useQueryClient();
  const notify = useNotify();
  const catalog = usePermissionCatalog();
  const role = editing.mode === 'edit' ? editing.role : undefined;
  const source = editing.mode === 'create' ? editing.from : undefined;
  const readOnly = !can('roles.manage') || (role ? isOwnerRole(role) : false);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(role?.permissions ?? source?.permissions ?? []),
  );
  const [filter, setFilter] = useState('');
  const [permError, setPermError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RoleValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: role?.name ?? (source ? `${source.name} (copy)` : ''),
      description: role?.description ?? source?.description ?? '',
    },
  });

  const groups = useMemo(() => groupPermissions(catalog.data?.data ?? []), [catalog.data]);
  /** Non-owners can only grant what they hold themselves. */
  const grantable = (key: string) => Boolean(me?.isOwner) || (me?.permissions ?? []).includes(key);

  const term = filter.trim().toLowerCase();
  const visible: PermissionGroup[] = term
    ? groups
        .map((g) => ({
          ...g,
          permissions: g.permissions.filter(
            (p) =>
              p.key.includes(term) ||
              p.description.toLowerCase().includes(term) ||
              g.label.toLowerCase().includes(term),
          ),
        }))
        .filter((g) => g.permissions.length > 0)
    : groups;

  const applyTemplate = (id: string) => {
    const t = ROLE_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    const keysInCatalog = new Set(groups.flatMap((g) => g.permissions.map((p) => p.key)));
    setSelected(new Set(t.permissions.filter((k) => keysInCatalog.has(k) && grantable(k))));
    setValue('name', t.name, { shouldValidate: true });
    setValue('description', t.description);
    setPermError(null);
  };

  const onSubmit = handleSubmit(async (v) => {
    if (selected.size === 0) {
      setPermError('Pick at least one permission');
      return;
    }
    const permissions = [...selected].sort();
    try {
      if (role) {
        await unwrap(
          api.PATCH('/roles/{id}', {
            params: { path: { id: role.id } },
            body: {
              name: v.name.trim(),
              description: v.description.trim() || null,
              permissions,
              version: role.version,
            },
          }),
        );
        notify.success(`${v.name} updated`);
      } else {
        await unwrap(
          api.POST('/roles', {
            params: idempotency(),
            body: {
              name: v.name.trim(),
              permissions,
              ...(v.description.trim() ? { description: v.description.trim() } : {}),
            },
          }),
        );
        notify.success(`${v.name} role created`);
      }
      await qc.invalidateQueries({ queryKey: keys.roles });
      await qc.invalidateQueries({ queryKey: keys.staff });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ROLE_NAME_TAKEN') {
        setError('name', { message: 'Another role already has this name' });
        return;
      }
      if (e instanceof ApiError && e.code === 'VERSION_CONFLICT') {
        await qc.invalidateQueries({ queryKey: keys.roles });
        notify.error('Someone else changed this role meanwhile. Close it and try again.');
        return;
      }
      if (!applyFieldErrors(e, ['name', 'description'], setError)) notify.error(e);
    }
  });

  const title = readOnly
    ? (role?.name ?? 'Role')
    : role
      ? `Edit ${role.name}`
      : source
        ? `Duplicate ${source.name}`
        : 'Create a role';

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={phone}
      slotProps={{ paper: { sx: { height: phone ? undefined : 'min(860px, 92vh)' } } }}
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {readOnly && <LockRounded sx={{ fontSize: 20, color: 'text.secondary' }} />}
            <span>{title}</span>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {readOnly
              ? role && isOwnerRole(role)
                ? 'The Owner role always has every permission and can’t be changed.'
                : 'You can view this role. Changing roles needs the “Manage roles” permission.'
              : 'Choose exactly what people with this role can see and do.'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, minHeight: 0 }}>
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(0,1fr) minmax(0,1.6fr)' },
              }}
            >
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    label="Role name"
                    autoFocus={!readOnly}
                    disabled={readOnly}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    label="Description"
                    placeholder="What is this role for?"
                    disabled={readOnly}
                    error={Boolean(errors.description)}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Box>

            {!readOnly && editing.mode === 'create' && !source && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Start from a template
                </Typography>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {ROLE_TEMPLATES.map((t) => (
                    <Tooltip key={t.id} title={t.description}>
                      <Chip
                        label={t.name}
                        variant="outlined"
                        icon={<KeyRounded />}
                        onClick={() => applyTemplate(t.id)}
                        sx={{ fontWeight: 600 }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Permissions
                </Typography>
                <Typography variant="caption" color={permError ? 'error' : 'text.secondary'}>
                  {permError ??
                    `${String(selected.size)} of ${String(catalog.data?.data.length ?? 0)} selected`}
                </Typography>
              </Box>
              <SearchField value={filter} onChange={setFilter} placeholder="Filter permissions…" />
            </Stack>

            {catalog.isLoading ? (
              <Stack spacing={1}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rounded" height={56} />
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: permError ? 'error.main' : 'divider',
                  borderRadius: '14px',
                  overflow: 'hidden',
                }}
              >
                {visible.map((g) => {
                  const state = groupState(g, selected);
                  const expanded = Boolean(term) || open.has(g.module);
                  const count = g.permissions.filter((p) => selected.has(p.key)).length;
                  const groupLocked = readOnly || g.permissions.some((p) => !grantable(p.key));
                  return (
                    <Box key={g.module} sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={(t) => ({
                          alignItems: 'center',
                          pl: 0.5,
                          pr: 1.5,
                          py: 0.5,
                          bgcolor:
                            state === 'none'
                              ? 'transparent'
                              : `color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} 4%, transparent)`,
                        })}
                      >
                        <Checkbox
                          checked={state === 'all'}
                          indeterminate={state === 'some'}
                          disabled={groupLocked}
                          onChange={() => {
                            setSelected(toggleGroup(g, selected));
                            setPermError(null);
                          }}
                          slotProps={{ input: { 'aria-label': `All ${g.label} permissions` } }}
                        />
                        <ButtonBase
                          onClick={() =>
                            setOpen((s) => {
                              const n = new Set(s);
                              if (n.has(g.module)) n.delete(g.module);
                              else n.add(g.module);
                              return n;
                            })
                          }
                          aria-expanded={expanded}
                          sx={{
                            flex: 1,
                            justifyContent: 'space-between',
                            py: 1,
                            borderRadius: '8px',
                          }}
                        >
                          <Typography sx={{ fontWeight: 650 }}>{g.label}</Typography>
                          <Typography
                            variant="caption"
                            className="tabular"
                            sx={{
                              fontWeight: 650,
                              color: count ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {count}/{g.permissions.length}
                          </Typography>
                        </ButtonBase>
                      </Stack>
                      <Collapse in={expanded} unmountOnExit>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'minmax(0,1fr)',
                              sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            px: 1,
                            pb: 1,
                          }}
                        >
                          {g.permissions.map((p) => {
                            const locked = readOnly || !grantable(p.key);
                            return (
                              <Stack
                                key={p.key}
                                component="label"
                                direction="row"
                                spacing={0.5}
                                sx={{
                                  alignItems: 'flex-start',
                                  py: 0.5,
                                  pr: 1,
                                  borderRadius: '10px',
                                  cursor: locked ? 'default' : 'pointer',
                                  '&:hover': { bgcolor: locked ? undefined : 'action.hover' },
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selected.has(p.key)}
                                  disabled={locked}
                                  onChange={(e) => {
                                    const n = new Set(selected);
                                    if (e.target.checked) n.add(p.key);
                                    else n.delete(p.key);
                                    setSelected(n);
                                    setPermError(null);
                                  }}
                                  slotProps={{ input: { 'aria-label': p.description } }}
                                />
                                <Box sx={{ minWidth: 0, pt: 0.75 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {permissionAction(p.key)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {p.description}
                                  </Typography>
                                </Box>
                              </Stack>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
                {visible.length === 0 && (
                  <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                    No permission matches “{filter}”.
                  </Typography>
                )}
              </Box>
            )}
            {!readOnly && !me?.isOwner && (
              <Alert severity="info" icon={<VisibilityRounded />}>
                You can only grant permissions your own roles include.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {readOnly ? (
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" loading={isSubmitting}>
                {role ? 'Save role' : 'Create role'}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
